import { useState, useEffect, useRef } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import {
  Menu,
  X,
  Home,
  Users,
  Settings,
  Bell,
  MapPin,
  Shield,
  BarChart3,
  CalendarClock,
  Table,
  Webhook,
  AlertTriangle,
  TrendingUp,
  BarChart2,
  ScrollText,
  UserPlus,
  Clock,
  DollarSign,
  Sparkles,
  ExternalLink,
  ClipboardCheck,
  ShieldCheck,
} from "lucide-react";
import { useAuthStore } from "../stores/authStore";
import { ACTION_KEYS, type ActionKey } from "../constants/actions";

interface LayoutProps {}

interface NavigationItem {
  name: string;
  href: string;
  icon: typeof Home;
  permissions: ActionKey[];
  external?: boolean;
}

const navigation: NavigationItem[] = [
  {
    name: "Dashboard",
    href: "/",
    icon: Home,
    permissions: [ACTION_KEYS.DASHBOARD_VIEW],
  },
  {
    name: "Cash Flow",
    href: "/cash-flow",
    icon: TrendingUp,
    permissions: [ACTION_KEYS.CASH_FLOW_VIEW],
  },
  {
    name: "Overages & Minimums",
    href: "/billable-overages",
    icon: BarChart2,
    permissions: [ACTION_KEYS.CASH_FLOW_VIEW],
  },
  {
    name: "Budgeting",
    href: "/budgeting",
    icon: BarChart3,
    permissions: [ACTION_KEYS.BUDGETING_VIEW],
  },
  {
    name: "Forecasting",
    href: "/budgeting-forecasting",
    icon: CalendarClock,
    permissions: [ACTION_KEYS.FORECASTING_VIEW],
  },
  {
    name: "Capture Rates",
    href: "/capture-rates",
    icon: Table,
    permissions: [ACTION_KEYS.CAPTURE_RATE_VIEW, ACTION_KEYS.BILLING_VIEW],
  },
  {
    name: "PCC Performance",
    href: "/pcc-performance",
    icon: Table,
    permissions: [ACTION_KEYS.PCC_PERFORMANCE_VIEW],
  },
  {
    name: "Hiring Needs",
    href: "/hiring-needs",
    icon: UserPlus,
    permissions: [ACTION_KEYS.HIRING_NEEDS_VIEW],
  },
  {
    name: "Clock In / Out",
    href: "/clock",
    icon: Clock,
    permissions: [ACTION_KEYS.CLOCK_SELF],
  },
  {
    name: "Clock Manager",
    href: "/clock/manager",
    icon: Clock,
    permissions: [ACTION_KEYS.CLOCK_MANAGE],
  },
  {
    name: "Timesheet Review (Team Lead)",
    href: "/timesheet-review/team-lead",
    icon: ClipboardCheck,
    permissions: [ACTION_KEYS.TIMESHEET_TL_REVIEW],
  },
  {
    name: "Timesheet Review (OM)",
    href: "/timesheet-review/om",
    icon: ShieldCheck,
    permissions: [ACTION_KEYS.TIMESHEET_OM_REVIEW],
  },
  {
    name: "PCC Schedule",
    href: "/pcc-schedule",
    icon: CalendarClock,
    permissions: [ACTION_KEYS.SCHEDULE_MANAGE],
  },
  {
    name: "Payroll",
    href: "/payroll",
    icon: DollarSign,
    permissions: [ACTION_KEYS.PAYROLL_VIEW],
  },
  {
    name: "AI Platform",
    href: "https://angela-fe.hytgenx.co/dashboard",
    icon: Sparkles,
    permissions: [ACTION_KEYS.AI_PLATFORM_VIEW],
    external: true,
  },
  {
    name: "Minutes Usage (Webhooks)",
    href: "/minutes-usage-webhooks",
    icon: Webhook,
    permissions: [ACTION_KEYS.BILLING_VIEW],
  },
  {
    name: "Unassigned Queues",
    href: "/unassigned-queues",
    icon: AlertTriangle,
    permissions: [ACTION_KEYS.BILLING_VIEW],
  },
  {
    name: "Customer Profiles",
    href: "/customer-profiles",
    icon: Users,
    permissions: [ACTION_KEYS.CUSTOMER_VIEW],
  },
  {
    name: "Team Members",
    href: "/team-members",
    icon: Users,
    permissions: [ACTION_KEYS.TEAM_MEMBER_MANAGE, ACTION_KEYS.TEAM_MEMBER_PAY],
  },
  {
    name: "Transcripts & Summaries",
    href: "/transcripts",
    icon: ScrollText,
    permissions: [ACTION_KEYS.TRANSCRIPT_VIEW],
  },
  {
    name: "Region & Teams",
    href: "/region-teams",
    icon: MapPin,
    permissions: [ACTION_KEYS.REGION_MANAGE],
  },
  {
    name: "Access Control",
    href: "/access-control",
    icon: Shield,
    permissions: [ACTION_KEYS.ACCESS_MANAGE],
  },
  {
    name: "Settings",
    href: "/settings",
    icon: Settings,
    permissions: [ACTION_KEYS.SETTINGS_MANAGE],
  },
];

export function Layout({}: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [pendingTimesheetReviews, setPendingTimesheetReviews] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timesheetReviewPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const location = useLocation();
  const { user, logout, hasPermission } = useAuthStore((state) => ({
    user: state.user,
    logout: state.logout,
    hasPermission: state.hasPermission,
  }));

  const canManageClock = hasPermission(ACTION_KEYS.CLOCK_MANAGE);
  const canClockSelf = hasPermission(ACTION_KEYS.CLOCK_SELF);

  useEffect(() => {
    if (!canManageClock) return;
    const fetchCount = () => {
      fetch("/api/v1/clock/pending-approvals/count", { credentials: "include" })
        .then((r) => r.json())
        .then((d) => setPendingApprovals(d?.count ?? 0))
        .catch(() => {});
    };
    fetchCount();
    pollRef.current = setInterval(fetchCount, 30_000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [canManageClock]);

  useEffect(() => {
    if (!canClockSelf) return;
    const fetchCount = () => {
      fetch("/api/v1/timesheet-review/pending", { credentials: "include" })
        .then((r) => r.json())
        .then((d) => setPendingTimesheetReviews(Array.isArray(d?.data) ? d.data.length : 0))
        .catch(() => {});
    };
    fetchCount();
    timesheetReviewPollRef.current = setInterval(fetchCount, 30_000);
    return () => { if (timesheetReviewPollRef.current) clearInterval(timesheetReviewPollRef.current); };
  }, [canClockSelf]);

  const permittedNavigation = navigation.filter((item) =>
    item.permissions.some((permission) => hasPermission(permission))
  );
  const activeNavigationItem = navigation.find(
    (item) => item.href === location.pathname
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${
          sidebarOpen ? "block" : "hidden"
        }`}
      >
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 flex w-64 max-w-[85vw] flex-col bg-white overflow-hidden">
          <div className="flex h-16 shrink-0 items-center justify-between px-4">
            <h1 className="text-lg font-semibold text-gray-900">
              Phone Billing
            </h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-1">
            {permittedNavigation.map((item) => {
              const isActive = !item.external && location.pathname === item.href;
              const isClockManager = item.href === "/clock/manager";
              const isClockSelf = item.href === "/clock";
              const isAIPlatform = item.external;
              const cls = `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                isAIPlatform
                  ? "text-purple-600 hover:bg-purple-50 hover:text-purple-800"
                  : isActive
                    ? "bg-blue-100 text-blue-900"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }`;
              const content = (
                <>
                  <item.icon className={`mr-3 h-5 w-5 ${isAIPlatform ? "text-purple-400" : isActive ? "text-blue-500" : "text-gray-400"}`} />
                  <span className="flex-1">{item.name}</span>
                  {isAIPlatform && <ExternalLink className="h-3.5 w-3.5 text-purple-300" />}
                  {isClockManager && pendingApprovals > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1 rounded-full bg-red-500 text-white text-xs font-bold">
                      {pendingApprovals}
                    </span>
                  )}
                  {isClockSelf && pendingTimesheetReviews > 0 && (
                    <span
                      className="ml-2 inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1 rounded-full bg-red-500 text-white text-xs font-bold"
                      title="Timesheet review due"
                    >
                      {pendingTimesheetReviews}
                    </span>
                  )}
                </>
              );
              return item.external ? (
                <a key={item.name} href={item.href} target="_blank" rel="noopener noreferrer" className={cls}>
                  {content}
                </a>
              ) : (
                <Link key={item.name} to={item.href} className={cls}>
                  {content}
                </Link>
              );
            })}
          </nav>
          <div className="border-t border-gray-200 p-4 shrink-0">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {user?.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="ml-3 min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">{user?.email}</p>
                <button
                  onClick={logout}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200 overflow-hidden">
          <div className="flex h-16 shrink-0 items-center px-4">
            <h1 className="text-lg font-semibold text-gray-900">
              Phone Billing
            </h1>
          </div>
          <nav className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-1">
            {permittedNavigation.map((item) => {
              const isActive = !item.external && location.pathname === item.href;
              const isClockManager = item.href === "/clock/manager";
              const isClockSelf = item.href === "/clock";
              const isAIPlatform = item.external;
              const cls = `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                isAIPlatform
                  ? "text-purple-600 hover:bg-purple-50 hover:text-purple-800"
                  : isActive
                    ? "bg-blue-100 text-blue-900"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }`;
              const content = (
                <>
                  <item.icon className={`mr-3 h-5 w-5 ${isAIPlatform ? "text-purple-400" : isActive ? "text-blue-500" : "text-gray-400"}`} />
                  <span className="flex-1">{item.name}</span>
                  {isAIPlatform && <ExternalLink className="h-3.5 w-3.5 text-purple-300" />}
                  {isClockManager && pendingApprovals > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1 rounded-full bg-red-500 text-white text-xs font-bold">
                      {pendingApprovals}
                    </span>
                  )}
                  {isClockSelf && pendingTimesheetReviews > 0 && (
                    <span
                      className="ml-2 inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1 rounded-full bg-red-500 text-white text-xs font-bold"
                      title="Timesheet review due"
                    >
                      {pendingTimesheetReviews}
                    </span>
                  )}
                </>
              );
              return item.external ? (
                <a key={item.name} href={item.href} target="_blank" rel="noopener noreferrer" className={cls}>
                  {content}
                </a>
              ) : (
                <Link key={item.name} to={item.href} className={cls}>
                  {content}
                </Link>
              );
            })}
          </nav>
          <div className="border-t border-gray-200 p-4 shrink-0">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {user?.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="ml-3 min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">{user?.email}</p>
                <button
                  onClick={logout}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        {/* Top bar */}
        <div className="sticky top-0 z-10 flex h-16 bg-white shadow">
          <button
            type="button"
            className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex flex-1 justify-between px-4 min-w-0">
            <div className="flex flex-1 min-w-0">
              <div className="flex w-full flex-col justify-center min-w-0">
                <div className="flex items-center min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 truncate">
                    {activeNavigationItem?.name || "Dashboard"}
                  </h2>
                </div>
              </div>
            </div>
            <div className="ml-4 flex items-center md:ml-6 shrink-0">
              <button
                type="button"
                className="rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <Bell className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="w-full px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
